import { useState } from "react"
import { useOvertime } from "@/contexts/overtime/useOvertime"
import RequestTable from "@/components/dashboard/RequestTable"
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useAuth } from "@/contexts/auth"
import { OvertimeStatus, TimeRange, UserRole as UserRoleEnum } from "@/types/enums"
import { getStatusColor } from "@/utils/statusUtils"

const MyRequests = () => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<string>(TimeRange.ALL)
  
  const { getUserRequests, loading } = useOvertime()
  
  const allRequests = getUserRequests()
  const pendingRequests = allRequests.filter(request => request.status === OvertimeStatus.PENDING)
  const approvedRequests = allRequests.filter(request => request.status === OvertimeStatus.APPROVED)
  const rejectedRequests = allRequests.filter(request => request.status === OvertimeStatus.REJECTED)
  const pendingVpRequests = allRequests.filter(request => request.status === OvertimeStatus.PENDING_VP_APPROVAL)
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Overtime Requests</h1>
          <p className="text-muted-foreground">
            View and manage your overtime requests
          </p>
        </div>
      </div>
      
      <Card>
        <CardHeader className="pb-0">
          <CardTitle>Overtime Requests</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <Tabs defaultValue={TimeRange.ALL} value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value={TimeRange.ALL}>
                All
                <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">
                  {allRequests.length}
                </span>
              </TabsTrigger>
              <TabsTrigger value={OvertimeStatus.PENDING}>
                Pending
                <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${getStatusColor(OvertimeStatus.PENDING, 'light')}`}>
                  {pendingRequests.length}
                </span>
              </TabsTrigger>
              {user?.role === UserRoleEnum.DEPARTMENT_HEAD && (
                <TabsTrigger value="pending_vp">
                  Pending by VP
                  <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${getStatusColor(OvertimeStatus.PENDING_VP_APPROVAL, 'light')}`}>
                    {pendingVpRequests.length}
                  </span>
                </TabsTrigger>
              )}
              <TabsTrigger value={OvertimeStatus.APPROVED}>
                Approved
                <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${getStatusColor(OvertimeStatus.APPROVED, 'light')}`}>
                  {approvedRequests.length}
                </span>
              </TabsTrigger>
              <TabsTrigger value={OvertimeStatus.REJECTED}>
                Rejected
                <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${getStatusColor(OvertimeStatus.REJECTED, 'light')}`}>
                  {rejectedRequests.length}
                </span>
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value={TimeRange.ALL}>
              <RequestTable 
                requests={allRequests} 
                title="My Overtime Requests" 
                hideTitle 
                isLoading={loading} 
              />
            </TabsContent>
            <TabsContent value={OvertimeStatus.PENDING}>
              <RequestTable 
                requests={pendingRequests} 
                title="My Pending Requests" 
                hideTitle 
                isLoading={loading} 
              />
            </TabsContent>
            {user?.role === UserRoleEnum.DEPARTMENT_HEAD && (
              <TabsContent value="pending_vp">
                <RequestTable 
                  requests={pendingVpRequests} 
                  title="My Requests Pending by VP" 
                  hideTitle 
                  isLoading={loading} 
                />
              </TabsContent>
            )}
            <TabsContent value={OvertimeStatus.APPROVED}>
              <RequestTable 
                requests={approvedRequests} 
                title="My Approved Requests" 
                hideTitle 
                isLoading={loading} 
              />
            </TabsContent>
            <TabsContent value={OvertimeStatus.REJECTED}>
              <RequestTable 
                requests={rejectedRequests} 
                title="My Rejected Requests" 
                hideTitle 
                isLoading={loading} 
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}

export default MyRequests
